import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    snoozeVirtualAppointment,
    updateVirtualAppointmentStatus,
    updateVirtualAppointmentTeam,
    type ManageableVirtualAppointmentStatus,
    type VirtualAppointmentSnoozeTrigger,
    type VirtualAppointmentTeam
} from '../../services/virtualAppointments';
import {
    PATIENT_VIRTUAL_APPOINTMENTS_KEY,
    VIRTUAL_APPOINTMENTS_OVERVIEW_KEY
} from '../../hooks/queryKeys';
import { apiErrorMessage } from '../../utils/apiError';

const STATUS_FALLBACK = "Couldn't update this VA. Try again.";
const TEAM_FALLBACK = "Couldn't change this VA's team. Try again.";
const SNOOZE_FALLBACK = "Couldn't snooze this VA. Try again.";

export interface VirtualAppointmentRowActions {
    pending: boolean;
    /** A status change (done / undone / working status) is in flight. */
    statusPending: boolean;
    statusError: string | null;
    teamError: string | null;
    snoozeError: string | null;
    setStatus: (status: ManageableVirtualAppointmentStatus, onSuccess?: () => void) => void;
    resetStatusError: () => void;
    setTeam: (team: VirtualAppointmentTeam) => void;
    snooze: (trigger: VirtualAppointmentSnoozeTrigger, onSuccess: () => void) => void;
    resetSnoozeError: () => void;
}

// Per-VA mutations. Success stays pending until the list refetches, so the row never shows a
// stale value with live controls; a failure refetches too since a 409 means the VA moved on.
export function useVirtualAppointmentRowActions(
    patientId: string,
    vaId: string
): VirtualAppointmentRowActions {
    const queryClient = useQueryClient();
    const refresh = useCallback(
        () =>
            Promise.all([
                queryClient.invalidateQueries({ queryKey: [VIRTUAL_APPOINTMENTS_OVERVIEW_KEY] }),
                queryClient.invalidateQueries({
                    queryKey: PATIENT_VIRTUAL_APPOINTMENTS_KEY(patientId)
                })
            ]),
        [queryClient, patientId]
    );
    const statusMutation = useMutation({
        mutationFn: (status: ManageableVirtualAppointmentStatus) =>
            updateVirtualAppointmentStatus(patientId, vaId, status),
        onSuccess: refresh,
        onError: () => void refresh()
    });
    const teamMutation = useMutation({
        mutationFn: (team: VirtualAppointmentTeam) =>
            updateVirtualAppointmentTeam(patientId, vaId, team),
        onSuccess: refresh,
        onError: () => void refresh()
    });
    const snoozeMutation = useMutation({
        mutationFn: (trigger: VirtualAppointmentSnoozeTrigger) =>
            snoozeVirtualAppointment(patientId, vaId, trigger),
        onSuccess: refresh,
        onError: () => void refresh()
    });

    const { mutate: mutateStatus, reset: resetStatus } = statusMutation;
    const { mutate: mutateTeam } = teamMutation;
    const { mutate: mutateSnooze, reset: resetSnooze } = snoozeMutation;
    const setStatus = useCallback(
        (status: ManageableVirtualAppointmentStatus, onSuccess?: () => void) =>
            mutateStatus(status, { onSuccess }),
        [mutateStatus]
    );
    const setTeam = useCallback((team: VirtualAppointmentTeam) => mutateTeam(team), [mutateTeam]);
    const snooze = useCallback(
        (trigger: VirtualAppointmentSnoozeTrigger, onSuccess: () => void) =>
            mutateSnooze(trigger, { onSuccess }),
        [mutateSnooze]
    );

    const pending = statusMutation.isPending || teamMutation.isPending || snoozeMutation.isPending;
    const statusError = statusMutation.isError
        ? apiErrorMessage(statusMutation.error, STATUS_FALLBACK)
        : null;
    const teamError = teamMutation.isError
        ? apiErrorMessage(teamMutation.error, TEAM_FALLBACK)
        : null;
    const snoozeError = snoozeMutation.isError
        ? apiErrorMessage(snoozeMutation.error, SNOOZE_FALLBACK)
        : null;
    return useMemo(
        () => ({
            pending,
            statusPending: statusMutation.isPending,
            statusError,
            teamError,
            snoozeError,
            setStatus,
            resetStatusError: resetStatus,
            setTeam,
            snooze,
            resetSnoozeError: resetSnooze
        }),
        [
            pending,
            statusMutation.isPending,
            statusError,
            teamError,
            snoozeError,
            setStatus,
            resetStatus,
            setTeam,
            snooze,
            resetSnooze
        ]
    );
}
